// Product grid: show every card photo with the product at the same visual size.
// The supplier photos have very different white margins, so we measure where the product sits inside
// each photo and center/scale it (CSS variables read by emeltec.css). If an image cannot be read
// (blocked, still loading, error) it is left exactly as it was.
(() => {
  const SELECTOR = '.product-grid .card-gallery img';
  const FILL = 0.78; // share of the square that the longest side of the product should cover
  const MIN_SCALE = 0.7;
  const MAX_SCALE = 2.2; // do not blow up tiny photos
  const SAMPLE = 96; // analysis canvas size (px)
  const DIFF = 60; // summed RGB distance from the background to count as product
  const fits = new Map(); // image path -> Promise<fit | null>

  const analyze = (src) =>
    new Promise((resolve) => {
      const probe = new Image();
      probe.crossOrigin = 'anonymous';
      probe.onerror = () => resolve(null);
      probe.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = SAMPLE;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(probe, 0, 0, SAMPLE, SAMPLE);
          const { data } = ctx.getImageData(0, 0, SAMPLE, SAMPLE);
          const px = (x, y) => (y * SAMPLE + x) * 4;
          const corners = [px(0, 0), px(SAMPLE - 1, 0), px(0, SAMPLE - 1), px(SAMPLE - 1, SAMPLE - 1)];
          const bg = [0, 1, 2].map((c) => corners.reduce((sum, i) => sum + data[i + c], 0) / 4);
          let x0 = SAMPLE, y0 = SAMPLE, x1 = -1, y1 = -1, hits = 0;
          for (let y = 0; y < SAMPLE; y++) {
            for (let x = 0; x < SAMPLE; x++) {
              const i = px(x, y);
              if (data[i + 3] < 20) continue;
              const d = Math.abs(data[i] - bg[0]) + Math.abs(data[i + 1] - bg[1]) + Math.abs(data[i + 2] - bg[2]);
              if (d < DIFF) continue;
              hits++;
              if (x < x0) x0 = x;
              if (x > x1) x1 = x;
              if (y < y0) y0 = y;
              if (y > y1) y1 = y;
            }
          }
          if (hits < SAMPLE) return resolve(null); // blank or almost blank photo
          resolve({
            x0: x0 / SAMPLE, x1: (x1 + 1) / SAMPLE, y0: y0 / SAMPLE, y1: (y1 + 1) / SAMPLE,
            aspect: probe.naturalWidth / probe.naturalHeight,
          });
        } catch (_) {
          resolve(null); // canvas tainted (no CORS)
        }
      };
      probe.src = src;
    });

  const apply = (img, box) => {
    // object-fit: contain letterboxes non-square photos inside the square card
    const rw = Math.min(1, box.aspect);
    const rh = Math.min(1, 1 / box.aspect);
    const bx0 = 0.5 + (box.x0 - 0.5) * rw;
    const bx1 = 0.5 + (box.x1 - 0.5) * rw;
    const by0 = 0.5 + (box.y0 - 0.5) * rh;
    const by1 = 0.5 + (box.y1 - 0.5) * rh;
    const scale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, FILL / Math.max(bx1 - bx0, by1 - by0)));
    const x = (0.5 - (bx0 + bx1) / 2) * scale * 100;
    const y = (0.5 - (by0 + by1) / 2) * scale * 100;
    img.style.transition = 'none';
    img.style.setProperty('--fit-scale', scale.toFixed(3));
    img.style.setProperty('--fit-x', `${x.toFixed(2)}%`);
    img.style.setProperty('--fit-y', `${y.toFixed(2)}%`);
    void img.offsetWidth; // commit the new transform without animating it
    img.style.transition = '';
  };

  const fit = (img) => {
    const src = img.currentSrc || img.src;
    if (!src || img.dataset.emeltecFit === src) return;
    img.dataset.emeltecFit = src;
    const key = src.split('?')[0];
    if (!fits.has(key)) fits.set(key, analyze(src));
    fits.get(key).then((box) => {
      if (box && img.isConnected && img.dataset.emeltecFit === src) apply(img, box);
    });
  };

  const scan = () => {
    document.querySelectorAll(SELECTOR).forEach((img) => {
      if (img.complete && img.naturalWidth) fit(img);
      else img.addEventListener('load', () => fit(img), { once: true });
    });
  };

  let queued = false;
  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  };

  const start = () => {
    scan();
    // filters, sorting and infinite scroll re-render the grid
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
